import { Capacitor } from '@capacitor/core';
import { AppLauncher, OpenURLResult } from '@capacitor/app-launcher';
import Compressor from 'compressorjs';
import { UserProfile } from '../interfaces/user';
import rgbHex from 'rgb-hex';
import hexRgb, { RgbaTuple } from 'hex-rgb';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { GroupedTransactions, OverviewTransaction, Transaction } from '../interfaces/wallet';
import { anykrowdApi } from '@/api/anykrowdApi';

// calculateShade
const calculateShade = (rgba: string, shadeFactor = 0.88): string => {
	const rgbaSplitted = rgba.split(',');

	const shaded = rgbaSplitted.map((color, index) => {
		if (index < 3) {
			return (Number(color) * shadeFactor).toFixed();
		}
		return color;
	});
	return shaded.toString();
};

const activateMagicLink = async (token: string): Promise<{ token: string; redirect_url: string }> => {
	const { data } = await anykrowdApi.post(`${window.apiUrl}/magic-link/${token}`, {}, false);
	return data;
};

// calculateTint
const calculateTint = (rgba: string): string => {
	const tintFactor = 0.1;
	const rgbaSplitted = rgba.split(',');

	const tinted = rgbaSplitted.map((color, index) => {
		if (index < 3) {
			return (Number(color) + (255 - Number(color)) * tintFactor).toFixed();
		}
		return color;
	});
	return tinted.toString();
};

const calculateLuminance = (colorRgb: RgbaTuple) => {
	const luminance = (0.299 * colorRgb[0] + 0.587 * colorRgb[1] + 0.114 * colorRgb[2]) / 255;
	const contrastColor = luminance > 0.5 ? '#000000' : '#ffffff';
	const contrastColorRgb = luminance > 0.5 ? '0, 0, 0' : '255, 255, 255';

	return {
		contrastColor,
		contrastColorRgb,
	};
};

// compressImage
const compressImage = (file: Blob, options?: Compressor.Options): Promise<Blob> => {
	return new Promise((resolve) => {
		if (Capacitor.isNativePlatform()) {
			resolve(file);
			return;
		}

		new Compressor(file, {
			quality: options?.quality || 0.6,
			...options,
			// The compression process is asynchronous,
			// which means you have to access the `result` in the `success` hook function.
			success(result) {
				resolve(result);
			},
			error(err) {
				console.log(err.message);
			},
		});
	});
};

// convertBase64ToBlob
const convertBase64ToBlob = (Base64Image: string) => {
	// split into two parts
	const parts = Base64Image.split(';base64,');
	// hold the content type
	const imageType = parts[0].split(':')[1];
	// decode base64 string
	const decodedData = window.atob(parts[1]);
	// create unit8array of size same as row data length
	const uInt8Array = new Uint8Array(decodedData.length);
	// insert all character code into uint8array
	for (let i = 0; i < decodedData.length; ++i) {
		uInt8Array[i] = decodedData.charCodeAt(i);
	}
	// return blob image after conversion
	return new Blob([uInt8Array], { type: imageType });
};

// convertBlobToBase64
const convertBlobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve, _) => {
		const fileReader = new FileReader();
		// eslint-disable-next-line @typescript-eslint/dot-notation
		const zoneOriginalInstance = (fileReader as any)['__zone_symbol__originalInstance'];
		const reader = zoneOriginalInstance || fileReader;

		reader.onloadend = () => resolve(reader.result.toString());
		reader.readAsDataURL(blob);
	});
};

// Convert value to currency
const convertToCurrency = (valueToConvert: number, locale = 'en-BE') => {
	return Intl.NumberFormat(locale, {
		style: 'decimal',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(valueToConvert);
};

const exchange = (amount: number, exchangeRate: number, toFiat = false): number => {
	if (toFiat) {
		return amount / exchangeRate;
	}

	return amount * exchangeRate;
};

// dataURLtoBlob
const dataURLtoBlob = (dataurl: string) => {
	const arr = dataurl.split(',');
	const mime = arr[0].match(/:(.*?);/)![1];
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	return new Blob([u8arr], { type: mime });
};

// getApiErrorString
const getApiErrorString = (
	error: any,
):
	| {
			error: string;
			errorCode?: string | null;
			status?: string | null;
	  }
	| string[] => {
	if (error.error?.error) {
		if (error.error.error === 'payment_provider_unavailable') {
			return {
				error: 'error.payment_provider_unavailable',
				errorCode: error.error.error_code ? error.error.error_code : 'unknown',
			};
		} else if (error.error.error === 'refund_not_cancellable') {
			return {
				error: 'error.refund_not_cancellable',
				status: error.error.status ? error.error.status : 'unknown',
			};
		}

		return {
			error: `error.${error.error.error}`,
		};
	}

	if (error.error?.errors) {
		return Object.keys(error.error.errors).map((fieldName) => error.error.errors[fieldName]);
	}

	return {
		error: 'error.general',
	};
};

const getCurrentWindowLocationOrigin = (): string => {
	if (Capacitor.isNativePlatform()) {
		const domain = new URL(window.apiUrl).origin;

		return domain;
	}

	return new URL(location.href).origin;
};

// getDataURLFileSize
const getDataURLFileSize = (dataUrl: string) => {
	const head = 'data:image/png;base64,';
	const fileSize = Math.round(((dataUrl.length - head.length) * 3) / 4) / 1024 / 1024;
	return fileSize;
};

const paymentMethodCountries = [
	{ value: 'BE', label: 'Belgium', flagClass: 'fi fi-be' },
	{ value: 'NL', label: 'Netherlands', flagClass: 'fi fi-nl' },
	{ value: 'LU', label: 'Luxembourg', flagClass: 'fi fi-lu' },
	{ value: 'DE', label: 'Germany', flagClass: 'fi fi-de' },
	{ value: 'FR', label: 'France', flagClass: 'fi fi-fr' },
	{ value: 'AT', label: 'Austria', flagClass: 'fi fi-at' },
	{ value: 'DK', label: 'Denmark', flagClass: 'fi fi-dk' },
	{ value: 'SE', label: 'Sweden', flagClass: 'fi fi-se' },
	{ value: 'FI', label: 'Finland', flagClass: 'fi fi-fi' },
	{ value: 'NO', label: 'Norway', flagClass: 'fi fi-no' },
	{ value: 'CH', label: 'Switzerland', flagClass: 'fi fi-ch' },
	{ value: 'GB', label: 'United Kingdom', flagClass: 'fi fi-gb' },
	{ value: 'IE', label: 'Ireland', flagClass: 'fi fi-ie' },
	{ value: 'PT', label: 'Portugal', flagClass: 'fi fi-pt' },
	{ value: 'ES', label: 'Spain', flagClass: 'fi fi-es' },
	{ value: 'IT', label: 'Italy', flagClass: 'fi fi-it' },
	{ value: 'GR', label: 'Greece', flagClass: 'fi fi-gr' },
	{ value: 'PL', label: 'Poland', flagClass: 'fi fi-pl' },
	{ value: 'CZ', label: 'Czech Republic', flagClass: 'fi fi-cz' },
	{ value: 'SK', label: 'Slovakia', flagClass: 'fi fi-sk' },
	{ value: 'HU', label: 'Hungary', flagClass: 'fi fi-hu' },
	{ value: 'SI', label: 'Slovenia', flagClass: 'fi fi-si' },
	{ value: 'HR', label: 'Croatia', flagClass: 'fi fi-hr' },
	{ value: 'RS', label: 'Serbia', flagClass: 'fi fi-rs' },
	{ value: 'BG', label: 'Bulgaria', flagClass: 'fi fi-bg' },
	{ value: 'RO', label: 'Romania', flagClass: 'fi fi-ro' },
	{ value: 'LT', label: 'Lithuania', flagClass: 'fi fi-lt' },
	{ value: 'LV', label: 'Latvia', flagClass: 'fi fi-lv' },
	{ value: 'EE', label: 'Estonia', flagClass: 'fi fi-ee' },
	{ value: 'MT', label: 'Malta', flagClass: 'fi fi-mt' },
	{ value: 'MA', label: 'Morocco', flagClass: 'fi fi-ma' },
];

const getAppLanguages = () => {
	return [
		{ value: 'en', label: 'languages.en', flagClass: 'fi fi-us', default: true },
		{ value: 'fr', label: 'languages.fr', flagClass: 'fi fi-fr' },
		{ value: 'pt', label: 'languages.pt', flagClass: 'fi fi-pt' },
		{ value: 'es', label: 'languages.es', flagClass: 'fi fi-es' },
		{ value: 'nl', label: 'languages.nl', flagClass: 'fi fi-nl' },
	];
};

// isOffline
const isOffline = (): boolean => {
	return !navigator.onLine;
};

// openURL
const openURL = async (url: string, options?: { target?: '_self' | '_blank' | '_parent' | '_top' }): Promise<OpenURLResult | Window | undefined> => {
	if (Capacitor.isNativePlatform()) {
		return await AppLauncher.openUrl({ url });
	}

	if (options?.target === '_blank') {
		window.open(url, options.target);
	}

	window.location.href = url;
};

// dragToScroll
const dragToScroll = (elementId: string) => {
	const scrollContainer = document.getElementById(elementId);
	let isMouseDown = false;
	let startX: number;
	let scrollLeft: number;

	scrollContainer!.addEventListener('mousedown', (e) => {
		isMouseDown = true;
		startX = e.pageX - scrollContainer!.offsetLeft;
		scrollLeft = scrollContainer!.scrollLeft;
	});

	scrollContainer!.addEventListener('mouseleave', () => {
		isMouseDown = false;
	});

	scrollContainer!.addEventListener('mouseup', () => {
		isMouseDown = false;
	});

	scrollContainer!.addEventListener('mousemove', (e) => {
		if (!isMouseDown) return;
		e.preventDefault();
		const x = e.pageX - scrollContainer!.offsetLeft;
		const walk = x - startX;
		scrollContainer!.scrollLeft = scrollLeft - walk;
	});
};

// replaceTopUpGeneralMessageVariables
const replaceTopUpGeneralMessageVariables = (message: string, userProfile: UserProfile) => {
	if (typeof message !== 'string') {
		return message;
	}

	message = message.replace(/:userFirstName/g, userProfile.firstname);
	message = message.replace(/:userLastName/g, userProfile.lastname);
	message = message.replace(/:userFullName/g, userProfile.full_name);
	message = message.replace(/:userEmail/g, userProfile.email);

	return message;
};

const setColors = () => {
	// Primary color
	// Hex
	const primaryRgb = hexRgb(window.primary_color, { format: 'array' });
	const primaryShade = '#' + rgbHex(calculateShade(primaryRgb.toString()));
	const primaryTint = '#' + rgbHex(calculateTint(primaryRgb.toString()));
	document.documentElement.style.setProperty('--ion-color-primary', window.primary_color);
	primaryRgb.splice(primaryRgb.length - 1, 1);
	// Rgb
	document.documentElement.style.setProperty('--ion-color-primary-rgb', primaryRgb.toString());
	// Shade
	document.documentElement.style.setProperty('--ion-color-primary-shade', primaryShade);
	// Tint
	document.documentElement.style.setProperty('--ion-color-primary-tint', primaryTint);

	// Secondary color
	const secondaryRgb = hexRgb(window.secondary_color, { format: 'array' });
	const secondaryShade = rgbHex(calculateShade(secondaryRgb.toString()));
	const secondaryTint = rgbHex(calculateTint(secondaryRgb.toString()));
	// Hex
	document.documentElement.style.setProperty('--ion-color-secondary', window.secondary_color);
	// Rgb
	secondaryRgb.splice(primaryRgb.length - 1, 1);
	document.documentElement.style.setProperty('--ion-color-secondary-rgb', secondaryRgb.toString());
	// Shade
	document.documentElement.style.setProperty('--ion-color-secondary-shade', secondaryShade);
	// Tint
	document.documentElement.style.setProperty('--ion-color-secondary-tint', secondaryTint);

	// Tertiary color
	const tertiaryRgb = hexRgb(window.tertiary_color, { format: 'array' }).toString();
	const tertiaryShade = rgbHex(calculateShade(tertiaryRgb.toString()));
	const tertiaryTint = rgbHex(calculateTint(tertiaryRgb.toString()));
	// Hex
	document.documentElement.style.setProperty('--ion-color-tertiary', window.tertiary_color);
	// Rgb
	secondaryRgb.splice(tertiaryShade.length - 1, 1);
	document.documentElement.style.setProperty('--ion-color-tertiary-rgb', tertiaryShade.toString());
	// Shade
	document.documentElement.style.setProperty('--ion-color-tertiary-shade', tertiaryShade);
	// Tint
	document.documentElement.style.setProperty('--ion-color-tertiary-tint', tertiaryTint);
};

const transformOembedToIframe = (content: string) => {
	// Replace content
	return content.replaceAll('<oembed url=', '<iframe src=').replaceAll('</oembed>', '</iframe>').replaceAll('https://www.youtube.com/watch?v=', 'https://youtube.com/embed/').replaceAll('></frame>', ' frameborder="0"; scrolling="no"; allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>');
};

const groupAndSumTransactionsByCurrency = (transactions: OverviewTransaction[] | null | undefined): GroupedTransactions => {
	if (!Array.isArray(transactions)) {
		return {};
	}

	return transactions.reduce((acc, transaction) => {
		if (transaction.type === 'top_up_cost') {
			return acc;
		}

		const { amount, currency, direction } = transaction;

		// For debit transactions, convert amount to negative for correct math in topups
		const amountNumber = parseFloat(amount) * (direction === 'DEBIT' ? -1 : 1);

		const currencyId = currency?.id ?? 0;
		const groupKey = `${currencyId}_${direction}`;

		if (!acc[groupKey]) {
			acc[groupKey] = {
				prefix: currency?.prefix,
				name: currency?.name,
				transactions: [],
				totalAmount: 0,
				totalFiatAmount: 0,
				type: transaction.type,
				reference: transaction.reference,
				direction: transaction.direction,
				currencyId: currencyId,
			};
		}

		acc[groupKey].transactions.push(transaction);
		acc[groupKey].totalAmount += amountNumber;
		if (currency) {
			acc[groupKey].totalFiatAmount += amountNumber * currency.exchange_rate;
		} else {
			acc[groupKey].totalFiatAmount += amountNumber;
		}

		return acc;
	}, {} as GroupedTransactions);
};

type T = ReturnType<typeof useI18n>['t'];
const toRelativeTime = (t: T, date: number) => {
	const now = DateTime.now();
	const walletUpdatedAtDate = DateTime.fromMillis(date);
	const diffInMilliseconds = now.diff(walletUpdatedAtDate).milliseconds;

	if (diffInMilliseconds <= 15000) return t('common.just_now');
	return DateTime.fromMillis(date).toRelative();
};

const useUtils = () => {
	return {
		activateMagicLink,
		calculateLuminance,
		calculateShade,
		calculateTint,
		compressImage,
		convertBase64ToBlob,
		convertBlobToBase64,
		convertToCurrency,
		dataURLtoBlob,
		dragToScroll,
		exchange,
		groupAndSumTransactionsByCurrency,
		getAppLanguages,
		getApiErrorString,
		getCurrentWindowLocationOrigin,
		getDataURLFileSize,
		isOffline,
		openURL,
		paymentMethodCountries,
		replaceTopUpGeneralMessageVariables,
		setColors,
		transformOembedToIframe,
		toRelativeTime,
	};
};

export default useUtils;
